import { Component, Input } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  CardModule,
  CheckboxModule,
  DropdownModule,
  RadioButtonModule,
  AlertModule,
  TooltipModule,
} from 'axis-components';
import { CommonModule, JsonPipe, SlicePipe } from '@angular/common';
import { BaseRules, TextValues } from './rhode-island-model';
import { UimuiBaseStateComponent } from '../uimui-base-state.component';
import { RequestService } from '../../../_services/request/request.service';
import { TruncateModule } from 'axis-components/truncate';
import { UimuiBaseRulesService } from '../services/uimui-base-rules.service';
import {
  CoverageType,
  LimitType,
  UIUMSelectionType,
} from '../../../config/constants';

@Component({
  selector: 'app-rhode-island',
  standalone: true,
  imports: [
    CardModule,
    CheckboxModule,
    CommonModule,
    DropdownModule,
    ReactiveFormsModule,
    RadioButtonModule,
    AlertModule,
    SlicePipe,
    TruncateModule,
    TooltipModule,
  ],
  templateUrl: './rhode-island.component.html',
  styleUrl: './rhode-island.component.scss',
})
export class RhodeIslandComponent extends UimuiBaseStateComponent<
  BaseRules,
  TextValues
> {
  constructor(
    private baseRuleService: UimuiBaseRulesService,
    private requestService: RequestService,
  ) {
    super();
  }

  @Input() set formGroup(form: FormGroup | null) {
    if (form) {
      super.getJsonRuleFiles('rhode-island', form);
    }
  }
  @Input() set isReview(review: boolean) {
    if (review) {
      super.getJsonRuleFiles('rhode-island', null);
    }
  }

  override baseRuleQueryBuilder() {
    if (this.baseRulesService.getNonGarageStates().includes(this.state)) {
      return this.baseRulesService.states.find(s => s.state === this.state)?.defaultQuery;
    };
    let liabilityLimit = this.requestService
      .getLiabilityLimitForState('rhode-island')
      .split('.')[0];
    liabilityLimit = parseFloat(liabilityLimit.replace(/[,$]/g, ''));

    const RejectWhenPossible = 'RejectWhenPossible';
    const StateMinimum = 'StateMinimum';
    const IndividualState = 'IndividualState';
    const PolicyLiabilityLimits = 'PolicyLiabilityLimits';
    const Mandatory = 'BodilyInjuryWithMandatoryPropertyDamage';
    const Optional = 'BodilyInjuryWithOptionalPropertyDamage';
    const Csl = 'CombinedSingleLimit';
    const Split = 'Split';
    const VaryBetweenStates = 'VaryBetweenStates';

    if (liabilityLimit > 75000) {
      if (
        this.baseRuleService.selectionTypeKey === RejectWhenPossible &&
        this.baseRuleService.coverageTypeKey === Mandatory &&
        this.baseRuleService.limitTypeKey === Csl
      ) {
        return 'Above_75k_Reject_Reject_Csl';
      } else if (
        this.baseRuleService.selectionTypeKey === StateMinimum &&
        this.baseRuleService.coverageTypeKey === Mandatory &&
        this.baseRuleService.limitTypeKey === Csl
      ) {
        return 'Above_75k_Reject_Reject_Csl';
      } else if (
        this.baseRuleService.selectionTypeKey === RejectWhenPossible &&
        this.baseRuleService.coverageTypeKey === Mandatory &&
        this.baseRuleService.limitTypeKey === Split
      ) {
        return 'Above_75k_Reject_Reject_Split';
      } else if (
        this.baseRuleService.selectionTypeKey === StateMinimum &&
        this.baseRuleService.coverageTypeKey === Mandatory &&
        this.baseRuleService.limitTypeKey === Split
      ) {
        return 'Above_75k_Reject_Reject_Split';
      } else if (
        this.baseRuleService.selectionTypeKey === RejectWhenPossible &&
        this.baseRuleService.coverageTypeKey === Mandatory &&
        this.baseRuleService.limitTypeKey === VaryBetweenStates
      ) {
        return 'Above_75k_Reject_Reject_Varybetween';
      } else if (
        this.baseRuleService.selectionTypeKey === StateMinimum &&
        this.baseRuleService.coverageTypeKey === Mandatory &&
        this.baseRuleService.limitTypeKey === VaryBetweenStates
      ) {
        return 'Above_75k_Reject_Reject_Varybetween';
      } else if (
        this.baseRuleService.selectionTypeKey === RejectWhenPossible &&
        this.baseRuleService.coverageTypeKey === Optional &&
        this.baseRuleService.limitTypeKey === Csl
      ) {
        return 'Above_75k_Reject_Optional_Csl';
      } else if (
        this.baseRuleService.selectionTypeKey === StateMinimum &&
        this.baseRuleService.coverageTypeKey === Optional &&
        this.baseRuleService.limitTypeKey === Csl
      ) {
        return 'Above_75k_Reject_Optional_Csl';
      } else if (
        this.baseRuleService.selectionTypeKey === RejectWhenPossible &&
        this.baseRuleService.coverageTypeKey === Optional &&
        this.baseRuleService.limitTypeKey === Split
      ) {
        return 'Above_75k_Reject_Optional_Split';
      } else if (
        this.baseRuleService.selectionTypeKey === StateMinimum &&
        this.baseRuleService.coverageTypeKey === Optional &&
        this.baseRuleService.limitTypeKey === Split
      ) {
        return 'Above_75k_Reject_Optional_Split';
      } else if (
        this.baseRuleService.selectionTypeKey === RejectWhenPossible &&
        this.baseRuleService.coverageTypeKey === Optional &&
        this.baseRuleService.limitTypeKey === VaryBetweenStates
      ) {
        return 'Above_75k_Reject_Optional_Varybetween';
      } else if (
        this.baseRuleService.selectionTypeKey === StateMinimum &&
        this.baseRuleService.coverageTypeKey === Optional &&
        this.baseRuleService.limitTypeKey === VaryBetweenStates
      ) {
        return 'Above_75k_Reject_Optional_Varybetween';
      } else if (
        this.baseRuleService.selectionTypeKey === IndividualState &&
        this.baseRuleService.coverageTypeKey === Mandatory &&
        this.baseRuleService.limitTypeKey === Csl
      ) {
        return 'Above_75k_Individual_Reject_Csl';
      } else if (
        this.baseRuleService.selectionTypeKey === IndividualState &&
        this.baseRuleService.coverageTypeKey === Mandatory &&
        this.baseRuleService.limitTypeKey === Split
      ) {
        return 'Above_75k_Individual_Reject_Split';
      } else if (
        this.baseRuleService.selectionTypeKey === IndividualState &&
        this.baseRuleService.coverageTypeKey === Mandatory &&
        this.baseRuleService.limitTypeKey === VaryBetweenStates
      ) {
        return 'Above_75k_Individual_Reject_Varybetween';
      } else if (
        this.baseRuleService.selectionTypeKey === IndividualState &&
        this.baseRuleService.coverageTypeKey === Optional &&
        this.baseRuleService.limitTypeKey === Csl
      ) {
        return 'Above_75k_Individual_Optional_Csl';
      } else if (
        this.baseRuleService.selectionTypeKey === IndividualState &&
        this.baseRuleService.coverageTypeKey === Optional &&
        this.baseRuleService.limitTypeKey === Split
      ) {
        return 'Above_75k_Individual_Optional_Split';
      } else if (
        this.baseRuleService.selectionTypeKey === IndividualState &&
        this.baseRuleService.coverageTypeKey === Optional &&
        this.baseRuleService.limitTypeKey === VaryBetweenStates
      ) {
        return 'Above_75k_Individual_Optional_Varybetween';
      } else if (
        this.baseRuleService.selectionTypeKey === PolicyLiabilityLimits &&
        this.baseRuleService.coverageTypeKey === Mandatory 
      ) {
        return 'Above_75k_PolicyLiabilityLimits_Reject';
      } else if (
        this.baseRuleService.selectionTypeKey === PolicyLiabilityLimits &&
        this.baseRuleService.coverageTypeKey === Optional
      ) {
        return 'Above_75k_PolicyLiabilityLimits_Optional';
      } else {
        return undefined;
      }
    } else if (liabilityLimit <= 75000) {
      if (this.baseRuleService.selectionTypeKey === RejectWhenPossible) {
        return 'Below_75k_Reject';
      } else if (
        this.baseRuleService.selectionTypeKey === StateMinimum &&
        this.baseRuleService.limitTypeKey === Csl
      ) {
        return 'Below_75k_Selectmin_Csl';
      } else if (
        this.baseRuleService.selectionTypeKey === StateMinimum &&
        this.baseRuleService.limitTypeKey === Split
      ) {
        return 'Below_75k_Selectmin_Split';
      } else if (
        this.baseRuleService.selectionTypeKey === StateMinimum &&
        this.baseRuleService.limitTypeKey === VaryBetweenStates
      ) {
        return 'Below_75k_Selectmin_Varybetween';
      } else if (
        this.baseRuleService.selectionTypeKey === IndividualState &&
        this.baseRuleService.coverageTypeKey === Mandatory &&
        this.baseRuleService.limitTypeKey === Csl
      ) {
        return 'Below_75k_Individual_Reject_Csl';
      } else if (
        this.baseRuleService.selectionTypeKey === IndividualState &&
        this.baseRuleService.coverageTypeKey === Mandatory &&
        this.baseRuleService.limitTypeKey === Split
      ) {
        return 'Below_75k_Individual_Reject_Split';
      } else if (
        this.baseRuleService.selectionTypeKey === IndividualState &&
        this.baseRuleService.coverageTypeKey === Mandatory &&
        this.baseRuleService.limitTypeKey === VaryBetweenStates
      ) {
        return 'Below_75k_Individual_Reject_Varybetween';
      } else if (
        this.baseRuleService.selectionTypeKey === IndividualState &&
        this.baseRuleService.coverageTypeKey === Optional &&
        this.baseRuleService.limitTypeKey === Csl
      ) {
        return 'Below_75k_Individual_Optional_Csl';
      } else if (
        this.baseRuleService.selectionTypeKey === IndividualState &&
        this.baseRuleService.coverageTypeKey === Optional &&
        this.baseRuleService.limitTypeKey === Split
      ) {
        return 'Below_75k_Individual_Optional_Split';
      } else if (
        this.baseRuleService.selectionTypeKey === IndividualState &&
        this.baseRuleService.coverageTypeKey === Optional &&
        this.baseRuleService.limitTypeKey === VaryBetweenStates
      ) {
        return 'Below_75k_Individual_Optional_Varybetween';
      } else if (
        this.baseRuleService.selectionTypeKey === PolicyLiabilityLimits &&
        this.baseRuleService.coverageTypeKey === Mandatory 
      ) {
        return 'Below_75k_PolicyLiabilityLimits_Reject';
      } else if (
        this.baseRuleService.selectionTypeKey === PolicyLiabilityLimits &&
        this.baseRuleService.coverageTypeKey === Optional
      ) {
        return 'Below_75k_PolicyLiabilityLimits_Optional';
      }else {
        return undefined;
      }
    } else {
      return undefined;
    }
  }
}
